import { useState, useEffect, useMemo } from 'react';
import { loadTimeseries, listSeries } from '../../../data/timeseriesClient';
import { matchSeriesByTicker } from '../../../data/seriesUtils';
import type { TimeRange } from '../components/charts/PriceChart';

export interface ClusterSegment {
    start: Date;
    end: Date;
    clusterId: number;
}

export interface ClusterStats {
    mean: number; // avg daily return
    sharpe: number;
}

export interface ClusterSeriesOption {
    id: string;
    name: string;
    series: string;
}

export interface ClusteringOverlay {
    segments: ClusterSegment[];
    clusterColors: Record<number, string>;
    clusterStats: Record<number, ClusterStats>;
    sortedClusterIds: number[];
}

// Handcrafted colormaps indexed by cluster count (worst → best).
// Keyed by number of clusters so each gets exactly the right palette.
export const COLORMAPS: Record<number, string[]> = {
    2: ['#cd0000', '#89ffb3'],
    3: ['#cd0000', '#edfff5', '#2ec868'],
    4: ['#c80000', '#ffe100', '#50e380', '#014a20'],
    5: ['#c80000', '#ffe100', '#edfff5', '#50e380', '#014a20'],
    6: ['#c80000', '#fb7c00', '#ffe100', '#edfff5', '#50e380', '#014a20'],
    7: [
        '#c80000',
        '#ff6a11',
        '#ffff00',
        '#edfff5',
        '#92dea8',
        '#21ac5a',
        '#055c1a',
    ],
    8: [
        '#c80000',
        '#ff6a11',
        '#ffc710',
        '#fff458',
        '#cdffdf',
        '#43c375',
        '#21ac5a',
        '#055c1a',
    ],
    9: [
        '#680000',
        '#c80000',
        '#ff6a11',
        '#ffc710',
        '#fff458',
        '#cdffdf',
        '#43c375',
        '#21ac5a',
        '#055c1a',
    ],
    10: [
        '#680000',
        '#c80000',
        '#ff6a11',
        '#ffc710',
        '#fff458',
        '#cdffdf',
        '#43c375',
        '#21ac5a',
        '#055c1a',
        '#1a4a1a',
    ],
};

// Fallback for >10 clusters: repeat the 10-color map
const COLORMAP_FALLBACK = COLORMAPS[10]!;

const RANGE_DAYS: Record<TimeRange, number> = {
    '1W': 7,
    '1M': 30,
    '3M': 90,
    '1Y': 365,
    ALL: 365 * 15,
};

// Active cluster for a day is point.data.cluster_id (plain number).
// Timestamp is point.timestamp (ISO string at the top level of the point).
function toSegments(rawPoints: any[]): ClusterSegment[] {
    const segments: ClusterSegment[] = [];

    for (const point of rawPoints) {
        const clusterId: number | undefined = point?.data?.cluster_id;
        const t =
            typeof point?.timestamp === 'string'
                ? new Date(point.timestamp)
                : null;
        if (clusterId === undefined || t === null) continue;

        const last = segments[segments.length - 1];
        if (last && last.clusterId === clusterId) {
            last.end = t;
        } else {
            segments.push({ start: t, end: t, clusterId });
        }
    }

    return segments;
}

// Extend each segment's end to the next segment's start to eliminate 1-day gaps
// between daily data points. The first segment's start is set to epoch so that
// candles fetched for any time range are never left before all segment starts.
function extendSegments(segments: ClusterSegment[]): ClusterSegment[] {
    return segments.map((seg, i) => ({
        ...seg,
        start: i === 0 ? new Date(0) : seg.start,
        end: segments[i + 1]?.start ?? new Date(),
    }));
}

// Sort cluster IDs by avg return ascending (worst → best), then assign colors
// from the handcrafted map for exactly this many clusters.
function buildColorMap(
    segments: ClusterSegment[],
    stats: Record<number, ClusterStats>
): { colors: Record<number, string>; sortedIds: number[] } {
    const ids = [...new Set(segments.map(s => s.clusterId))];
    ids.sort((a, b) => (stats[a]?.mean ?? 0) - (stats[b]?.mean ?? 0));
    const colormap = COLORMAPS[ids.length] ?? COLORMAP_FALLBACK;
    const colors: Record<number, string> = {};
    ids.forEach((id, idx) => {
        colors[id] =
            colormap[idx] ?? COLORMAP_FALLBACK[idx % COLORMAP_FALLBACK.length];
    });
    return { colors, sortedIds: ids };
}

// cluster_stats lives on every point (same values), so just read from the last one.
function extractStats(rawPoints: any[]): Record<number, ClusterStats> {
    const stats: any[] =
        rawPoints[rawPoints.length - 1]?.data?.cluster_stats ?? [];
    const record: Record<number, ClusterStats> = {};
    for (const s of stats) {
        if (typeof s.cluster_id === 'number') {
            record[s.cluster_id] = { mean: s.mean ?? 0, sharpe: s.sharpe ?? 0 };
        }
    }
    return record;
}

export function useAssetClustering(
    ticker: string | null | undefined,
    timeRange: TimeRange,
    // Preferred series name (e.g. 'agg_4_btc'). If null, falls back to the first
    // series matchSeriesByTicker returns for this ticker.
    preferredName: string | null = null
): {
    overlay: ClusteringOverlay | null;
    loading: boolean;
    available: boolean;
    availableSeries: ClusterSeriesOption[];
} {
    const [rawPoints, setRawPoints] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    // null = not yet resolved, false = no series found for this ticker
    const [seriesId, setSeriesId] = useState<string | null | false>(null);
    const [availableSeries, setAvailableSeries] = useState<
        ClusterSeriesOption[]
    >([]);

    useEffect(() => {
        if (!ticker) {
            setSeriesId(false);
            return;
        }
        // Reset immediately so the fetch effect doesn't re-fire with a stale seriesId
        // while this effect is still resolving the correct one.
        setSeriesId(null);
        listSeries('clustering')
            .then(series => {
                const tickerSuffix = `_${ticker.toLowerCase()}`;
                const matching = series.filter((s: any) =>
                    s.id.toLowerCase().endsWith(tickerSuffix)
                );
                setAvailableSeries(matching);
                let match = preferredName
                    ? series.find((s: any) => s.id === preferredName) ?? null
                    : null;
                if (!match) match = matchSeriesByTicker(series, ticker);
                setSeriesId(match ? match.series : false);
            })
            .catch(() => setSeriesId(false));
    }, [ticker, preferredName]);

    useEffect(() => {
        if (!seriesId) {
            setRawPoints([]);
            return;
        }

        const controller = new AbortController();
        setLoading(true);

        const end = new Date();
        const start = new Date(
            end.getTime() - RANGE_DAYS[timeRange] * 24 * 60 * 60 * 1000
        );

        loadTimeseries(seriesId, {
            from: start,
            to: end,
            signal: controller.signal,
        })
            .then(data => setRawPoints(data))
            .catch(err => {
                if (err.name !== 'AbortError')
                    console.error('useAssetClustering: fetch failed', err);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [seriesId, timeRange]);

    const overlay = useMemo((): ClusteringOverlay | null => {
        if (!rawPoints.length) return null;
        const raw = toSegments(rawPoints);
        if (!raw.length) return null;
        const segments = extendSegments(raw);
        const clusterStats = extractStats(rawPoints);
        const { colors, sortedIds } = buildColorMap(segments, clusterStats);
        return {
            segments,
            clusterColors: colors,
            clusterStats,
            sortedClusterIds: sortedIds,
        };
    }, [rawPoints]);

    return {
        overlay,
        loading,
        available: seriesId !== false,
        availableSeries,
    };
}
